import React, { createContext, useContext, useState, useEffect } from 'react';
import { notifyIdentityChanged } from '../services/identity';
import { useNavigate } from 'react-router-dom';

/*
 * Borrowing somebody else's account, and giving it back.
 *
 * This used to hold exactly one identity in reserve: the admin's, saved under
 * `original_token` while they were a client. Two things broke that. A super can
 * now become a Solution Partner, and from inside that partner they can become
 * one of the partner's clients, which is two borrowings deep. With a single
 * slot the second one overwrote the first, so stepping back out of the client
 * dropped you at the partner with no way home and no banner saying you were
 * still somebody else.
 *
 * So the identities are a stack. Starting pushes who you are now; exiting pops
 * one layer and restores it, which lands a super back at the partner, and the
 * next exit lands them back at themselves. `isImpersonating` is simply whether
 * the stack has anything in it.
 *
 * The old single-slot keys are still written, because other screens read them
 * directly (`impersonated_client_id` on the marketplace pages, and
 * `original_user_role` where a surface is only for staff). They now mirror the
 * stack: `original_user_role` is the role at the BOTTOM, the real human.
 */

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedClientId: string | null;
  impersonatedUserId: string | null;
  originalUserId: string | null;
  originalToken: string | null;
}

/** One identity held in reserve while a borrowed one is in use. */
interface Frame {
  token: string;
  userId: string;
  role: string;
  name: string;
  email: string;
  clientId: string | null;
  /** Where exiting back INTO this identity should land. */
  returnTo: string;
}

interface ImpersonationTarget {
  access_token: string;
  user: { user_id: string; client_id: string | null; name?: string; email?: string };
  /** The aibot role of whoever is being become. Defaults to 'client'. */
  role?: string;
  /** Where the borrowed session starts. Defaults to /dashboard. */
  land?: string;
  /** Where Exit returns to. Defaults to the consultant home. */
  returnTo?: string;
}

interface ImpersonationContextType {
  impersonationState: ImpersonationState;
  startImpersonation: (data: ImpersonationTarget) => void;
  endImpersonation: () => Promise<void>;
}

const STACK_KEY = 'impersonation_stack';

const readStack = (): Frame[] => {
  try {
    const raw = localStorage.getItem(STACK_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStack = (frames: Frame[]) => {
  if (frames.length === 0) localStorage.removeItem(STACK_KEY);
  else localStorage.setItem(STACK_KEY, JSON.stringify(frames));
};

const defaultState: ImpersonationState = {
  isImpersonating: false,
  impersonatedClientId: null,
  impersonatedUserId: null,
  originalUserId: null,
  originalToken: null
};

const stateFromStorage = (): ImpersonationState => {
  const stack = readStack();
  if (stack.length === 0) return defaultState;
  return {
    isImpersonating: true,
    impersonatedClientId: localStorage.getItem('impersonated_client_id'),
    impersonatedUserId: localStorage.getItem('impersonated_user_id'),
    originalUserId: stack[0].userId,
    originalToken: stack[0].token,
  };
};

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [impersonationState, setImpersonationState] = useState<ImpersonationState>(defaultState);

  useEffect(() => { setImpersonationState(stateFromStorage()); }, []);

  const startImpersonation = (data: ImpersonationTarget) => {
    const stack = readStack();
    stack.push({
      token: localStorage.getItem('auth_token') || '',
      userId: localStorage.getItem('user_id') || '',
      role: localStorage.getItem('user_role') || 'consultant',
      name: localStorage.getItem('user_name') || '',
      email: localStorage.getItem('user_email') || '',
      clientId: localStorage.getItem('client_id'),
      returnTo: data.returnTo || '/consultant/home',
    });
    writeStack(stack);

    // Mirrors for the screens that read these directly.
    localStorage.setItem('impersonation_token', data.access_token);
    localStorage.setItem('impersonated_user_id', data.user.user_id);
    localStorage.setItem('original_token', stack[0].token);
    localStorage.setItem('original_user_id', stack[0].userId);
    localStorage.setItem('original_user_role', stack[0].role);

    // Become them.
    localStorage.setItem('auth_token', data.access_token);
    localStorage.setItem('user_id', data.user.user_id);
    localStorage.setItem('user_role', data.role || 'client');

    /* Every shell greets whoever user_name says it is, so leaving it alone
       would keep greeting the admin by name over somebody else's account. */
    if (data.user.name) localStorage.setItem('user_name', data.user.name);
    if (data.user.email) localStorage.setItem('user_email', data.user.email);

    /* A Solution Partner belongs to no company, so a client_id left over from
       whoever was signed in before would scope every read to the wrong one. */
    if (data.user.client_id) {
      localStorage.setItem('impersonated_client_id', data.user.client_id);
      localStorage.setItem('client_id', data.user.client_id);
    } else {
      localStorage.removeItem('impersonated_client_id');
      localStorage.removeItem('client_id');
    }
    localStorage.removeItem('client_name');

    setImpersonationState({
      isImpersonating: true,
      impersonatedClientId: data.user.client_id,
      impersonatedUserId: data.user.user_id,
      originalUserId: stack[0].userId,
      originalToken: stack[0].token,
    });

    // Same-tab identity change (no reload) — tell the shell to reconcile.
    notifyIdentityChanged();
    navigate(data.land || '/dashboard');
  };

  const endImpersonation = async () => {
    const stack = readStack();
    const frame = stack.pop();
    if (!frame) return;

    /* Kill the builder (validx) session cookie — the borrowing minted a bridge
       session we don't want lingering once the account is handed back. */
    try {
      const builderUrl =
        (process.env.REACT_APP_BUILDER_URL as string | undefined) ||
        'https://builder.agentsanywhere.ai';
      await fetch(`${builderUrl}/api/auth/clear-session`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
      });
    } catch {
      // Non-fatal — continue the local reset even if builder is unreachable.
    }

    writeStack(stack);

    localStorage.setItem('auth_token', frame.token);
    localStorage.setItem('user_id', frame.userId);
    localStorage.setItem('user_role', frame.role);
    localStorage.setItem('user_name', frame.name);
    localStorage.setItem('user_email', frame.email);
    if (frame.clientId) localStorage.setItem('client_id', frame.clientId);
    else localStorage.removeItem('client_id');
    localStorage.removeItem('client_name');

    if (stack.length === 0) {
      localStorage.removeItem('impersonation_token');
      localStorage.removeItem('impersonated_user_id');
      localStorage.removeItem('impersonated_client_id');
      localStorage.removeItem('original_token');
      localStorage.removeItem('original_user_id');
      localStorage.removeItem('original_user_role');
      setImpersonationState(defaultState);
    } else {
      /* Still inside a borrowed account, one layer down. The mirrors describe
         that layer, not the one just handed back. */
      localStorage.setItem('impersonation_token', frame.token);
      localStorage.setItem('impersonated_user_id', frame.userId);
      if (frame.clientId) localStorage.setItem('impersonated_client_id', frame.clientId);
      else localStorage.removeItem('impersonated_client_id');
      localStorage.setItem('original_token', stack[0].token);
      localStorage.setItem('original_user_id', stack[0].userId);
      localStorage.setItem('original_user_role', stack[0].role);
      setImpersonationState({
        isImpersonating: true,
        impersonatedClientId: frame.clientId,
        impersonatedUserId: frame.userId,
        originalUserId: stack[0].userId,
        originalToken: stack[0].token,
      });
    }

    // Same-tab identity change (no reload) — tell the shell to reconcile.
    notifyIdentityChanged();
    navigate(frame.returnTo);
  };

  return (
    <ImpersonationContext.Provider value={{ impersonationState, startImpersonation, endImpersonation }}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};
